import { useMutation, useQueryClient } from '@tanstack/react-query';
import { rpcClient } from 'src/api/rpc/client';
import {
  useCreateTaskComment,
  useTaskComments,
} from 'src/pages/authenticated/inbox/hooks/use-task-comments';

/**
 * A task's notes for the AR task feed: the thread, and posting, editing and
 * deleting a note. Edit and delete go through the generic comment
 * procedures, which only let a note's author change it.
 */
export function useArTaskNotes(taskId: string) {
  const queryClient = useQueryClient();
  const thread = useTaskComments(taskId);
  const create = useCreateTaskComment(taskId);
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['task-comments', taskId] });
  const update = useMutation({
    mutationFn: (input: { id: string; body: string }) =>
      rpcClient.comments.update(input),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => rpcClient.comments.delete({ id }),
    onSuccess: refresh,
  });
  return {
    comments: thread.data?.comments ?? [],
    isLoading: thread.isLoading,
    create,
    onUpdate: async (id: string, body: string) => {
      await update.mutateAsync({ id, body });
    },
    onDelete: async (id: string) => {
      await remove.mutateAsync(id);
    },
  };
}
